import _ from "lodash";
import update from "immutability-helper";
import React from "react";
import {
  Avatar,
  AvatarGroup,
  ButtonDisclosure,
  ChecklistDropdown,
  DatePicker,
  Dropdown,
  DropdownMenu,
  IconButton,
} from "@toddle-edu/ds-web";
import { DeleteOutlined } from "@toddle-edu/ds-icons";
import classNames from "classnames";
import classes from "./DynamicTableRow.scss";
import { useI18n } from "Hooks";
import {FormTextAreaInput} from "UIComponents/TextAreaInput";
import moment from "moment-timezone";

const styles = {
  tableData: {
    padding: 0,
  },
};

const TextAreaComponent = ({
  value,
  cellComponentConfig,
  rowNumber,
  disabled,
  updateFieldValueInCache,
  testId,
  column,
  rowId,
}) => {
  const { t } = useI18n(["common"]);
  const [textValue, setTextValue] = React.useState(value);

  const {
    styles: textAreaStyles = {},
    minRows = 1,
    placeholderLocale,
    placeholderSubLocale,
    type,
    showCountInPlaceHolder,
  } = cellComponentConfig;

  const handleInputUpdate = value => {
    let newValue = "";
    switch (type) {
      case "number": {
        newValue = value.replace(/\D/g, "");
        break;
      }
      default: {
        newValue = value;
      }
    }
    setTextValue(newValue);
    updateFieldValueInCache(newValue);
  };

  const showCountInPlaceHolderSubLocale = showCountInPlaceHolder
    ? { count: rowNumber }
    : {};

  return (
    <FormTextAreaInput
      value={textValue}
      textAreaStyle={textAreaStyles}
      minRows={minRows}
      placeholder={t(placeholderLocale, {
        label: t(placeholderSubLocale),
        ...showCountInPlaceHolderSubLocale,
      })}
      updateInputField={handleInputUpdate}
      disabled={disabled}
      dsVersion="2.0"
      testId={`${testId}-${column}-${rowId}`}
    />
  );
};

const DateSelectorComponent = ({
  value,
  updateFieldValueInCache,
  disabled,
  displayDisabledAsActive,
  testId,
  rowId,
  column,
}) => {
  const { t } = useI18n(["common"]);

  /*******************************************************************************************************
   * displayDisabledAsActive flag is used to control the visual state of DateSelectorComponent,
   * where if it is true, we will show the element in visually active form but
   * functionality wise it will behave like disabled.
   ********************************************************************************************************/

  const handleDatePickerChange = updatedValue => {
    if (displayDisabledAsActive && disabled) return;
    updateFieldValueInCache(updatedValue);
  };

  const dateSelectorContainerClass = classNames({
    [classes.container]: true,
    [classes.disabledAsActive]: displayDisabledAsActive && disabled,
  });

  return (
    <div className={dateSelectorContainerClass}>
      <DatePicker
        onChange={handleDatePickerChange}
        placeholder={t("common:select")}
        picker="date"
        value={moment(value)}
        variant="inline"
        name={"date"}
        disabled={disabled}
        testId={`${testId}-${column}-${rowId}`}
        dsVersion="2.0"
      />
    </div>
  );
};

const DropdownComponent = ({
  value,
  cellComponentConfig,
  updateFieldValueInCache,
  disabled,
  testId,
  column,
  rowId,
}) => {
  const {
    dropdownValues = [],
    dropDownConfig: { placement = "bottomRight" } = {},
    buttonDisclosureConfig: { isFullWidth = true } = {},
  } = cellComponentConfig;
  const { t } = useI18n(["common"]);

  const getDropDownOptions = _.memoize(() =>
    _.map(dropdownValues, value => ({
      ...value,
      label: t(value?.label),
    }))
  );

  return (
    <div className={classes.container}>
      <Dropdown
        placement={placement}
        getPopupContainer={el => {
          return el;
        }}
        overlay={
          <DropdownMenu
            options={getDropDownOptions()}
            onClick={item => updateFieldValueInCache(item?.key)}
            testId={`${testId}-${column}-${rowId}`}
            dsVersion="2.0"
          />
        }
        disabled={disabled}
      >
        <ButtonDisclosure
          className="relative"
          type="plain"
          variant="neutral"
          isFullWidth={isFullWidth}
          testId={`${testId}-${column}-${rowId}`}
        >
          <span>
            {_.find(getDropDownOptions(), { key: value })?.label ?? (
              <span
                className={classNames(classes.textPlaceholder, "text-body")}
              >
                {t("common:select")}
              </span>
            )}
          </span>
        </ButtonDisclosure>
      </Dropdown>
    </div>
  );
};

const CheckListDropdownComponent = ({
  value,
  cellComponentConfig,
  options,
  updateFieldValueInCache,
  disabled,
  testId,
  rowId,
  column,
}) => {
  const { t } = useI18n(["common"]);

  const {
    avatarGroupConfig: { max = 3, size = "small" } = {},
    buttonDisclosureConfig: { isFullWidth = true } = {},
    dropdownUiType,
  } = cellComponentConfig;

  let buttonDisclosureComponent = null;

  switch (dropdownUiType) {
    case "AVATAR_GROUP": {
      const users = _.filter(options, item => _.includes(value, item?.value));

      buttonDisclosureComponent = !_.isEmpty(users) && (
        <AvatarGroup size={size} max={max}>
          {_.map(users, user => (
            <Avatar
              key={user?.value}
              name={user?.label}
              src={user?.profileImage}
            />
          ))}
        </AvatarGroup>
      );
    }
  }

  return (
    <div className={classes.container}>
      <ChecklistDropdown
        options={options}
        onChange={updateFieldValueInCache}
        value={value ?? []}
        disabled={disabled}
        testId={`${testId}-${column}-${rowId}`}
      >
        <ButtonDisclosure
          className="relative"
          variant="neutral"
          type="plain"
          isFullWidth={isFullWidth}
          testId={`${testId}-${column}-${rowId}`}
        >
          {buttonDisclosureComponent ? (
            buttonDisclosureComponent
          ) : (
            <span className={classNames(classes.textPlaceholder, "text-body")}>
              {t("common:select")}
            </span>
          )}
        </ButtonDisclosure>
      </ChecklistDropdown>
    </div>
  );
};

const TextLabelComponent = ({ value }) => {
  const { t } = useI18n(["project", "common"]);
  const label = _.get(value, "label", "");
  const subText = _.get(value, "subText", "");

  return (
    <div className={classes.textLabelContainer}>
      {label && <div className="text-body"> {t(label)}</div>}
      {subText && (
        <div className={classNames("text-body-s", classes.textSecondary)}>
          {t(subText)}
        </div>
      )}
    </div>
  );
};

const getColumnComponent = props => {
  const { type } = props;

  switch (type) {
    case "TEXT_FIELD":
      return <TextAreaComponent {...props} />;

    case "DATE":
      return <DateSelectorComponent {...props} />;

    case "DROPDOWN":
      return <DropdownComponent {...props} />;

    case "CHECKLIST_DROPDOWN":
      return <CheckListDropdownComponent {...props} />;

    case "TEXT_LABEL":
      return <TextLabelComponent {...props} />;

    default:
      return null;
  }
};

const DynamicTableRow = ({
  rowData,
  columns,
  columnConfig,
  rowConfig,
  updateInputField,
  rowIndex,
  onClickDelete,
  totalRows,
  showDeleteColumn,
  disabled = false,
  displayDisabledAsActive = false,
  testId,
}) => {
  const { styles: rowStyles = {} } = rowConfig;

  return (
    <tr key={rowData?.id} style={rowStyles}>
      {_.map(columns, column => {
        const {
          type,
          cellStyle = {},
          cellComponentConfig = {},
          dropdownValueType,
          dropdownUiType = "",
          options,
        } = columnConfig[column];

        const value = _.get(rowData, `value[${column}]`, null);

        const updateFieldValueInCache = newValue => {
          const currentValue = rowData?.value || {};
          const updatedValue = update(currentValue, {
            [column]: value => update(value || "", { $set: newValue }),
          });

          updateInputField({
            [rowData?.uid]: updatedValue,
          });
        };

        return (
          <td
            style={{
              ...cellStyle,
              ...(type === "TEXT_FIELD" && styles.tableData),
            }}
            key={`${rowData?.id}_${column}`}
          >
            {getColumnComponent({
              type,
              value,
              cellComponentConfig,
              dropdownValueType,
              column,
              options,
              dropdownUiType,
              rowNumber: rowIndex + 1,
              updateFieldValueInCache,
              disabled,
              displayDisabledAsActive,
              testId,
              rowId: rowData?.id,
            })}
          </td>
        );
      })}
      {showDeleteColumn && (
        <td style={styles.tableData}>
          <div className={classes.deleteContainer}>
            <IconButton
              icon={<DeleteOutlined variant="subtle" />}
              type="plain"
              variant="neutral"
              onClick={() =>
                onClickDelete({
                  fieldId: rowData?.id,
                  totalRows,
                  fieldUid: rowData?.uid,
                })
              }
              disabled={disabled}
              testId={`${testId}-delete-${rowData?.id}`}
            />
          </div>
        </td>
      )}
    </tr>
  );
};

export default DynamicTableRow;
